// WbCustomerSearch — shared searchable customer picker for WB pages.
//
// The text input drives a SERVER-SIDE search: the debounced query is sent as
// `?search=` to GET /customers (the endpoint LIKE-matches name/email/phone/
// postcode — see listCustomersQuerySchema + customers.queries.ts), so the
// picker is no longer limited to a pre-loaded slice. `limit` is still capped at
// 100 server-side; the returned matches are shown (active-only filtering stays
// client-side since the endpoint exposes no is_active param).
//
// Used by:
//   - WbContractDetailPage (required, create flow)
//   - WbTicketsPage         (optional, list filter)
//   - WbReportsPage         (optional, report filter)
//   - WbTicketLeftRail      (ticket customer selection)

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';

interface CustomerOption {
  enc_id: string;
  name: string;
  account_status?: 'active' | 'inactive' | 'suspended';
}

interface CustomersListResponse {
  customers: Array<CustomerOption>;
}

export interface WbCustomerSearchProps {
  value: string | null;
  onChange: (encId: string | null, name: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  ariaLabel?: string;
  /** Optional id passed through to the select element for label association. */
  id?: string;
  /**
   * Display name for the currently-selected `value` when it isn't present in
   * the loaded list. Entity enc_ids are re-encrypted with a random IV on every
   * server response (see server/shared/crypto.ts), so a `value` taken from a
   * different response (e.g. the ticket DTO's `customer.enc_id`) never matches
   * an `<option>` keyed by the /customers list ciphertext — and a native
   * <select> would then drop the selection. It also covers a selected customer
   * filtered out by the active-only / search filter. When supplied, we inject a
   * matching option so the selection always shows.
   */
  selectedName?: string | null;
}

export function WbCustomerSearch({
  value,
  onChange,
  placeholder = 'Search customers…',
  disabled = false,
  required = false,
  ariaLabel = 'Customer',
  id,
  selectedName = null,
}: WbCustomerSearchProps): JSX.Element {
  const [filter, setFilter] = useState('');

  // 300ms debounce — typing fast shouldn't fire one request per keystroke. The
  // debounced value is what we send to the server (and key the query by).
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(filter.trim()), 300);
    return () => window.clearTimeout(handle);
  }, [filter]);

  const customersQuery = useQuery<CustomersListResponse>({
    queryKey: queryKeys.customers.list({
      limit: 100,
      ...(debounced !== '' ? { search: debounced } : {}),
    }),
    queryFn: async () => {
      const params: Record<string, string | number> = { limit: 100 };
      if (debounced !== '') params['search'] = debounced;
      const res = await apiClient.get<CustomersListResponse>(
        endpoints.customers.list,
        { params },
      );
      return res.data;
    },
    staleTime: 60_000,
  });

  const customers = customersQuery.data?.customers ?? [];
  // Filter out anything explicitly inactive/suspended. The server doesn't
  // surface an `is_active` query param, so this is a client-side filter.
  // Rows missing `account_status` (older test fixtures, partial payloads)
  // are kept — defaulting to "include" prevents false negatives.
  const activeOnly = useMemo(
    () =>
      customers.filter(
        (c) =>
          c.account_status === undefined || c.account_status === 'active',
      ),
    [customers],
  );

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (q === '') return activeOnly.slice(0, 100);
    return activeOnly
      .filter((c) => c.name.toLowerCase().includes(q))
      .slice(0, 100);
  }, [activeOnly, filter]);

  // S1 guard: if the server somehow stops emitting enc_id again (post-fix
  // regression), surface a clear warning instead of a broken picker.
  const missingEncId =
    customers.length > 0 &&
    customers.some(
      (c) => typeof c.enc_id !== 'string' || c.enc_id.length === 0,
    );

  if (missingEncId) {
    if (typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn(
        '[WbCustomerSearch] CustomerSummary response is missing enc_id. ' +
          'Selection is disabled until the server patch lands.',
      );
    }
    return (
      <div role="alert" data-testid="wb-customer-search-unavailable">
        ⚠ Customer selection unavailable until server patch lands.
      </div>
    );
  }

  function handleChange(nextEncId: string) {
    if (nextEncId === '') {
      onChange(null, null);
      return;
    }
    const picked = customers.find((c) => c.enc_id === nextEncId);
    onChange(nextEncId, picked?.name ?? null);
  }

  // When the selected `value` isn't among the rendered options (rotated
  // ciphertext from another response, or filtered out), inject it so the
  // native <select> keeps showing the selection instead of snapping to the
  // placeholder. Labelled with the caller-supplied name when known.
  const selectedInList =
    value !== null && filtered.some((c) => c.enc_id === value);
  const showInjectedSelected = value !== null && !selectedInList;

  return (
    <div data-testid="wb-customer-search">
      <input
        type="search"
        placeholder={placeholder}
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        disabled={disabled || customersQuery.isLoading}
        aria-label={`Filter ${ariaLabel.toLowerCase()} list`}
      />
      <select
        id={id}
        value={value ?? ''}
        onChange={(e) => handleChange(e.target.value)}
        disabled={disabled || customersQuery.isLoading}
        required={required}
        aria-label={ariaLabel}
      >
        <option value="">— Select a customer —</option>
        {showInjectedSelected ? (
          <option value={value ?? ''}>
            {selectedName ?? 'Selected customer'}
          </option>
        ) : null}
        {filtered.map((c) => (
          <option key={c.enc_id} value={c.enc_id}>
            {c.name}
          </option>
        ))}
      </select>
    </div>
  );
}
